import { FileWarning, ShieldAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { type ModalComponentProps } from '@/shared/components/Modals/modal-registry/modal-registry.types';
import { Button } from '@/shared/components/ui/Button/Button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/Dialog/Dialog';
import { Label } from '@/shared/components/ui/Label/Label';
import { Textarea } from '@/shared/components/ui/Textarea/Textarea';

import { useStepVerdictModalLogic } from './useStepVerdictModalLogic';

/**
 * Refusing a step, or skipping it.
 *
 * One dialog for both, because they are the same dialog with different copy —
 * see `useStepVerdictModalLogic` for why, and `EvidenceStepCard` for where the
 * permission difference actually lives.
 *
 * The **waive** wording says what a waiver is: a reviewer overriding the
 * deployment's own policy for one member, written to the audit log with their
 * name on it. That sentence is the reason the reason field is required, and it
 * belongs on the dialog rather than in a comment somebody has to go and read.
 */
export function StepVerdictModal({ open, onCancel, modalData }: ModalComponentProps) {
  const { t } = useTranslation(['verification', 'common']);
  const view = useStepVerdictModalLogic(modalData, onCancel);

  if (view.target === null) {
    return null;
  }

  const isWaive = view.target.verdict === 'waive';
  const scope = isWaive ? 'waiveStep' : 'failStep';
  const member = view.target.member ?? t('verification:queue.missingAccount');
  const step = t(`verification:step.key.${view.target.stepKey}`);
  const Icon = isWaive ? ShieldAlert : FileWarning;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
    >
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>{t(`verification:${scope}.title`)}</DialogTitle>
          <DialogDescription>{t('verification:detail.subtitle')}</DialogDescription>
        </DialogHeader>

        <DialogBody>
          <p className="text-body text-foreground">
            {t(`verification:${scope}.lead`, { member, step })}
          </p>

          {isWaive ? (
            <p className="flex items-start gap-sm text-body text-muted-foreground">
              <Icon aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{t('verification:waiveStep.audited')}</span>
            </p>
          ) : null}

          <div className="flex flex-col gap-xs">
            <Label htmlFor="verification-step-reason">
              {t(`verification:${scope}.reasonLabel`)}
            </Label>
            <Textarea
              id="verification-step-reason"
              aria-describedby="verification-step-reason-hint"
              value={view.reason}
              maxLength={1000}
              onChange={(event) => {
                view.setReason(event.target.value);
              }}
            />
            <p id="verification-step-reason-hint" className="text-caption text-muted-foreground">
              {t(`verification:${scope}.reasonHint`)}
            </p>
          </div>

          {view.error === null ? null : (
            <p role="alert" className="text-caption text-destructive">
              {view.error}
            </p>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" onClick={onCancel}>
            {t('common:action.cancel')}
          </Button>
          <Button
            variant={isWaive ? 'primary' : 'destructive'}
            isLoading={view.isSending}
            disabled={!view.canSubmit}
            onClick={() => {
              void view.submit();
            }}
          >
            {t(`verification:${scope}.submit`)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
